import Phaser from 'phaser';
import { getStore } from '@/infra/store';
import { logger } from '@/infra/logger/Logger';
import { isCreatureGalleryDebugMode } from '@/dev/debug-mode';
import { HouseScene } from './HouseScene';
import { WorldScene } from './WorldScene';
import { ExpeditionScene } from './ExpeditionScene';
import { CreatureGalleryScene } from './CreatureGalleryScene';
import { regionRegistry } from '@/domain/world/region-registry';
import meadowManifest from '@/assets/generated/world/meadow/meadow-manifest.json';
import forestManifest from '@/assets/generated/world/forest/forest-manifest.json';
import caveManifest from '@/assets/generated/world/cave/cave-manifest.json';
import coastManifest from '@/assets/generated/world/coast/coast-manifest.json';
import type { LayeredRegionBundle } from '@/domain/world/region-assets';

const regionManifests = {
  meadow: meadowManifest,
  forest: forestManifest,
  cave: caveManifest,
  coast: coastManifest,
} as const;

export class BootScene extends Phaser.Scene {
  static readonly KEY = 'BootScene';

  constructor() {
    super({ key: BootScene.KEY });
  }

  preload(): void {
    for (const region of regionRegistry.all()) {
      if (region.renderer.kind === 'layered-raster') {
        this.load.json(region.renderer.manifestUrl, region.renderer.manifestUrl);
        const manifest = regionManifests[region.biome] as LayeredRegionBundle;
        const sources = new Set([
          manifest.base,
          ...manifest.belowActors.map((layer) => layer.src),
          ...manifest.ySortedProps.map((layer) => layer.src),
          ...manifest.aboveActors.map((layer) => layer.src),
        ]);
        for (const source of sources) this.load.image(source, source);
      }
    }
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#0a0a0a');
    getStore().setReady(true);
    logger.info('boot.ready', { scene: BootScene.KEY });

    if (isCreatureGalleryDebugMode()) {
      if (!this.scene.get(CreatureGalleryScene.KEY)) {
        this.scene.add(CreatureGalleryScene.KEY, CreatureGalleryScene, false);
      }
      this.scene.start(CreatureGalleryScene.KEY);
      return;
    }

    if (!this.scene.get(HouseScene.KEY)) {
      this.scene.add(HouseScene.KEY, HouseScene, false);
    }
    if (!this.scene.get(WorldScene.KEY)) {
      this.scene.add(WorldScene.KEY, WorldScene, false);
    }
    if (!this.scene.get(ExpeditionScene.KEY)) {
      this.scene.add(ExpeditionScene.KEY, ExpeditionScene, false);
    }
    this.scene.start(HouseScene.KEY);
  }
}
